// /auth/start — landing page hands off here after the diagnostic funnel.
//
// URL shape: /auth/start?diagnostic=TOKEN&email=name@example.com&lang=en
//
// What we do:
//   1. Show a confirmation card with the email pre-filled.
//   2. On click, fire a magic-link with `callbackURL` that includes the
//      diagnostic token. After the user clicks the link in their email,
//      Better Auth 302s them to /auth/callback?diagnostic=TOKEN where the
//      token is exchanged for a seeded case.
//
// We do NOT auto-fire the magic-link on mount: that would email anyone whose
// link gets shared. The user explicitly clicks "Send" with their email
// visible — same posture as /login, just with their email already filled.

import { Link, createRoute } from '@tanstack/react-router';
import { useSignIn } from '../auth';
import { m } from '../paraglide/messages';
import { rootRoute } from './root';

export const authStartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/start',
  component: AuthStartRoute,
  validateSearch: (s: Record<string, unknown>) => ({
    diagnostic: typeof s.diagnostic === 'string' ? s.diagnostic : undefined,
    email: typeof s.email === 'string' ? s.email : undefined,
    lang: s.lang === 'pt' ? 'pt' : 'en',
  }),
});

function AuthStartRoute() {
  const signIn = useSignIn();
  const { diagnostic, email } = authStartRoute.useSearch();

  // Without the basics we can't proceed — punt to the regular login screen.
  if (!email || !diagnostic) {
    return (
      <div className="card narrow">
        <h1>{m.authStartCantContinueHeading()}</h1>
        <p className="muted">
          {/* The translated body interpolates a Link; render around the {signInLink} marker. */}
          {(() => {
            const body = m.authStartCantContinueBody({ signInLink: '__LINK__' });
            const [before, after] = body.split('__LINK__');
            return (
              <>
                {before}
                <Link to="/login">{m.authStartSignInDifferent()}</Link>
                {after}
              </>
            );
          })()}
        </p>
      </div>
    );
  }

  const onSend = () => {
    signIn.mutate({
      email,
      callbackURL: `${window.location.origin}/auth/callback?diagnostic=${encodeURIComponent(diagnostic)}`,
    });
  };

  if (signIn.isSuccess) {
    return (
      <div className="card narrow">
        <h1>{m.authStartSent()}</h1>
        <p className="muted">{m.authStartSentBody({ email })}</p>
      </div>
    );
  }

  return (
    <div className="card narrow">
      <h1>{m.authStartHeading()}</h1>
      <p className="muted">{m.authStartBody({ email })}</p>
      <button type="button" onClick={onSend} disabled={signIn.isPending}>
        {signIn.isPending ? m.authStartSending() : m.authStartSendButton()}
      </button>
      {signIn.isError ? <div className="notice error">{m.loginErrorGeneric()}</div> : null}
      <p className="fineprint" style={{ marginTop: 12 }}>
        {m.authStartWrongEmail()} <Link to="/login">{m.authStartSignInDifferent()}</Link>{' '}
        {m.authStartSignInDifferentSuffix()}
      </p>
    </div>
  );
}
